import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faGauge,
  faClipboardList,
  faPlus,
  faUsers,
  faFileLines,
  faEnvelope,
  faChartBar,
  faBullseye,
  faUser,
  faShieldHalved,
  faGear,
  faUsersGear,
  faFolderOpen,
  faWrench,
  faSitemap,
  faBookOpen,
  faIdBadge,
  faAddressCard,
  faEyeSlash,
  faScaleBalanced,
  faChartColumn,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useUserRoles } from '../auth';

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconDefinition;
  ariaKey: string;
  roles?: string[];
  /** Exact-match the route rather than prefix-match. */
  end?: boolean;
  /** i18n key. Renders a pill after the label when present. */
  badgeKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',         labelKey: 'nav.dashboard',         icon: faGauge,          ariaKey: 'nav.dashboardAria',       roles: ['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN'] },

  // Redesigned tender flow. Runs in parallel with the classic list below for the
  // verification window; the badge is what distinguishes the two at a glance.
  { to: '/v2/tenders',        labelKey: 'nav.tenders',           icon: faClipboardList,  ariaKey: 'nav.tendersAria',         roles: ['TENDER_VIEWER', 'TENDER_MANAGER'], badgeKey: 'nav.badgeNew' },

  // Legacy tender list. Retired in Increment 2 — the label carries the qualifier so
  // that removal is a deletion, not a rename of the surviving entry.
  { to: '/tenders',           labelKey: 'nav.tendersClassic',    icon: faClipboardList,  ariaKey: 'nav.tendersClassicAria',  roles: ['TENDER_VIEWER', 'TENDER_MANAGER'] },

  { to: '/tenders/submit',    labelKey: 'nav.submitTender',      icon: faPlus,           ariaKey: 'nav.submitTenderAria',    roles: ['TENDER_MANAGER'], end: true },
  { to: '/employees',         labelKey: 'nav.employees',         icon: faUsers,          ariaKey: 'nav.employeesAria',       roles: ['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/project-reports',   labelKey: 'nav.projectReports',    icon: faFolderOpen,     ariaKey: 'nav.projectReportsAria',  roles: ['EMPLOYEE', 'PROJECT_ADMIN', 'HR_ADMIN'] },
  { to: '/cvs',               labelKey: 'nav.cvManagement',      icon: faFileLines,      ariaKey: 'nav.cvManagementAria',    roles: ['TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/v2/mailbox',        labelKey: 'nav.mailbox',           icon: faEnvelope,       ariaKey: 'nav.mailboxAria',         roles: ['TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/mailbox',           labelKey: 'nav.mailboxClassic',    icon: faEnvelope,       ariaKey: 'nav.mailboxClassicAria',  roles: ['TENDER_MANAGER'] },
  { to: '/analytics',         labelKey: 'nav.analytics',         icon: faChartBar,       ariaKey: 'nav.analyticsAria',       roles: ['HR_ADMIN'] },
  { to: '/skill-development', labelKey: 'nav.skillDevelopment',  icon: faBullseye,       ariaKey: 'nav.skillDevelopmentAria', roles: ['HR_ADMIN'] },
  { to: '/my-team',           labelKey: 'nav.myTeam',            icon: faUsersGear,      ariaKey: 'nav.myTeamAria',          roles: ['EMPLOYEE'] },
  { to: '/my-skills',         labelKey: 'nav.mySkills',          icon: faUser,           ariaKey: 'nav.mySkillsAria',        roles: ['EMPLOYEE'] },
  { to: '/dpo',               labelKey: 'nav.dpoDashboard',      icon: faShieldHalved,   ariaKey: 'nav.dpoDashboardAria',    roles: ['DPO'] },
  { to: '/admin',             labelKey: 'nav.admin',             icon: faGear,           ariaKey: 'nav.adminAria',           roles: ['ADMIN'] },
  { to: '/admin/skills',      labelKey: 'nav.skillAdmin',        icon: faWrench,         ariaKey: 'nav.skillAdminAria',      roles: ['ADMIN', 'PROJECT_ADMIN'] },
  { to: '/admin/skills-taxonomy', labelKey: 'nav.skillsTaxonomy', icon: faSitemap,       ariaKey: 'nav.skillsTaxonomyAria',  roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/skills-library',  labelKey: 'nav.skillsLibrary',  icon: faBookOpen,      ariaKey: 'nav.skillsLibraryAria',   roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/skill-trials/blind',      labelKey: 'nav.trialBlind',      icon: faEyeSlash,       ariaKey: 'nav.trialBlindAria',      roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/skill-trials/runs',       labelKey: 'nav.trialRuns',       icon: faPlay,           ariaKey: 'nav.trialRunsAria',       roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/skill-trials/adjudicate', labelKey: 'nav.trialAdjudicate', icon: faScaleBalanced,  ariaKey: 'nav.trialAdjudicateAria', roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/skill-trials/report',     labelKey: 'nav.trialReport',     icon: faChartColumn,    ariaKey: 'nav.trialReportAria',     roles: ['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/persona-taxonomy', labelKey: 'nav.personaTaxonomy', icon: faIdBadge,     ariaKey: 'nav.personaTaxonomyAria',  roles: ['TENDER_MANAGER', 'ADMIN'] },
  { to: '/admin/persona-library',  labelKey: 'nav.personaLibrary',  icon: faAddressCard, ariaKey: 'nav.personaLibraryAria',   roles: ['TENDER_MANAGER', 'ADMIN'] },
];

export default function Layout() {
  const location = useLocation();
  const { t } = useTranslation();
  const { roles: userRoles, loaded: rolesLoaded } = useUserRoles();

  // Filter nav items by role. When auth is disabled (no roles loaded / empty roles), show all items.
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.roles || item.roles.length === 0) return true;
    if (!rolesLoaded || userRoles.length === 0) return true; // auth disabled — show all
    if (userRoles.includes('ADMIN')) return true;
    return item.roles.some((r) => userRoles.includes(r));
  });

  // Derive page title from pathname for the <title> element
  const activeItem = NAV_ITEMS.find((item) =>
    location.pathname === item.to ||
    (item.to !== '/dashboard' && location.pathname.startsWith(item.to))
  );
  const pageTitle = activeItem ? `${t(activeItem.labelKey)} — StaffLens` : 'StaffLens';

  // Update document title reactively
  if (typeof document !== 'undefined') {
    document.title = pageTitle;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ----------------------------------------------------------------
          Sidebar navigation
          ---------------------------------------------------------------- */}
      <nav
        aria-label={t('nav.mainNavigation')}
        className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-gray-200 shadow-sm z-10"
      >
        {/* Logo / branding */}
        <div className="flex h-16 items-center gap-2.5 px-6 border-b border-gray-100">
          <img src="/logo.png" alt="" className="h-8 rounded-lg object-contain" aria-hidden="true" />
          <span className="text-lg font-bold text-brand-700 tracking-tight">
            StaffLens
          </span>
        </div>

        {/* Nav links */}
        <ul className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                aria-label={t(item.ariaKey)}
                end={item.end}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  ].join(' ')
                }
              >
                <FontAwesomeIcon icon={item.icon} className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t(item.labelKey)}
                {item.badgeKey && (
                  <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-100 text-brand-700">
                    {t(item.badgeKey)}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-400">{t('common.version')}</p>
          <LanguageSwitcher />
        </div>
      </nav>

      {/* ----------------------------------------------------------------
          Mobile top bar
          ---------------------------------------------------------------- */}
      <header className="md:hidden fixed top-0 inset-x-0 z-20 flex h-14 items-center justify-between bg-white border-b border-gray-200 px-4 shadow-sm">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-7 rounded-md object-contain" aria-hidden="true" />
          <span className="text-base font-bold text-brand-700">StaffLens</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {/* Icon strip — no room for a text pill, so badges are not rendered here. */}
          <nav aria-label={t('nav.mobileNavigation')} className="flex gap-3">
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={t(item.ariaKey)}
                end={item.end}
                className={({ isActive }) =>
                  [
                    'text-lg',
                    isActive ? 'text-brand-600' : 'text-gray-500',
                  ].join(' ')
                }
              >
                <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* ----------------------------------------------------------------
          Main content area
          ---------------------------------------------------------------- */}
      <div className="flex flex-col flex-1 md:pl-64">
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 px-4 py-6 md:px-8 md:py-8 mt-14 md:mt-0 focus:outline-none"
        >
          <Outlet />
        </main>

        <footer className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400 text-center">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </footer>
      </div>
    </div>
  );
}
